import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import DayPicker, { type DateRange } from '@rio-cloud/rio-uikit/DayPicker';
import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import Select from '@rio-cloud/rio-uikit/Select';
import { useMemo } from 'react';

export type DateTimeRange = {
    from: string;
    to: string;
};

export type DateTimeRangeFieldProps = {
    id: string;
    label: string;
    placeholder: string;
    value: DateTimeRange;
    onChange: (value: DateTimeRange) => void;
    notBefore?: string;
};

/**
 * US month-first date with a 12-hour clock, matching the rest of the app's `en-US` formatting.
 *
 * These are date-fns tokens, which is what `DayPicker` formats the input with. The lowercase `hh` and
 * the `a` are outside the set the UIKIT's input mask understands, but that only applies in `single`
 * mode: in `range` mode the input is read-only and never masked, so the format is display only.
 */
const DATE_TIME_FORMAT = 'MM/dd/yyyy hh:mm a';

/** The app is US only, so the calendar follows `en-US` conventions, including a Sunday week start. */
const DATE_LOCALE = 'en-US';

const isValidDate = (date: Date): boolean => !Number.isNaN(date.getTime());

const toDate = (iso: string): Date | undefined => {
    if (iso === '') {
        return;
    }

    const date = new Date(iso);

    return isValidDate(date) ? date : undefined;
};

const toIso = (date: Date | undefined): string => (date && isValidDate(date) ? date.toISOString() : '');

const isMidnight = (date: Date): boolean =>
    date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0;

export const closeOpenEndedDay = (date: Date | undefined): Date | undefined => {
    if (!date || !isValidDate(date) || !isMidnight(date)) {
        return date;
    }

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 0, 0);

    return endOfDay;
};

const toRange = (value: DateTimeRange): DateRange | undefined => {
    const from = toDate(value.from);
    const to = toDate(value.to);

    return from || to ? { from, to } : undefined;
};

const toTimeValue = (date: Date | undefined): string => {
    if (!date || !isValidDate(date)) {
        return '';
    }

    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

/**
 * Writes an `HH:mm` time onto a day, keeping the calendar date and moving only the clock. Returns the
 * day unchanged while the time is being typed and is not yet a full `HH:mm`.
 */
export const applyTime = (date: Date | undefined, time: string): Date | undefined => {
    if (!date) {
        return date;
    }

    // A full `HH:mm` is two non-empty numeric parts. Guarding on the raw strings first, because
    // `Number('')` is 0, so a half-typed `09:` would otherwise read as 09:00.
    const [rawHours, rawMinutes] = time.split(':');

    if (!rawHours || !rawMinutes) {
        return date;
    }

    const hours = Number(rawHours);
    const minutes = Number(rawMinutes);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return date;
    }

    const next = new Date(date);
    next.setHours(hours, minutes, 0, 0);

    return next;
};

export const startOfDay = (date: Date): Date => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);

    return start;
};

export const isPast = (date: Date | undefined, now: Date): boolean =>
    date !== undefined && date.getTime() < now.getTime();

export const isEndpointUnusable = (iso: string, earliest: Date): boolean => {
    if (iso === '') {
        return false;
    }

    const date = toDate(iso);

    return date === undefined || isPast(date, earliest);
};

const TIME_SLOT_MINUTES = 15;

const MINUTES_PER_DAY = 24 * 60;

const toTimeLabel = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Renders a canonical `HH:mm` slot as a 12-hour label, for example `13:15` as `1:15 PM`.
 *
 * Slots stay 24 hour everywhere they are compared, sorted or written onto a day - `applyTime` parses
 * them, `isSlotBefore` orders them and `localeCompare` on `HH:mm` is chronological, none of which
 * holds for a 12-hour string. Only the text the user reads is converted.
 */
export const toTwelveHourLabel = (time: string): string => {
    const [rawHours, rawMinutes] = time.split(':');
    const hours = Number(rawHours);

    if (!rawHours || !rawMinutes || Number.isNaN(hours)) {
        return time;
    }

    const period = hours < 12 ? 'AM' : 'PM';
    // Midnight and noon are both written as 12 rather than 0.
    const twelveHour = hours % 12 === 0 ? 12 : hours % 12;

    return `${twelveHour}:${rawMinutes} ${period}`;
};

/** A slot label is before `earliest` when placing it on `day` lands strictly earlier than `earliest`. */
const isSlotBefore = (day: Date, label: string, earliest: Date): boolean => {
    const at = applyTime(day, label);

    return at !== undefined && at.getTime() < earliest.getTime();
};

/**
 * The times that may be chosen for `day`, given the earliest instant the window may open.
 *
 * `earliest` is the later of "now" and any caller-supplied floor (delivery cannot precede pickup). On
 * the boundary day only the slots at or after it are offered; on a later day the whole day is open.
 * `TimePicker` cannot express this - it generates every slot internally with no way to filter - so the
 * times are enumerated here and offered as a list, the "available times" pattern from the UIKIT docs.
 *
 * `keepValue` is included even when it is off the slot grid, so editing an order stored at, say, 08:07
 * does not lose its own time. It is dropped when it falls before `earliest`.
 */
export const timeSlotsFor = (day: Date | undefined, earliest: Date, keepValue?: string): string[] => {
    if (!day || !isValidDate(day)) {
        return [];
    }

    // Any day at or before the floor's day can hold slots that precede it, so filter those. A later
    // day is entirely open. This also covers a stale order's disabled past day, whose value the
    // calendar still seeds into the draft.
    const restrict = startOfDay(day).getTime() <= startOfDay(earliest).getTime();

    const slots: string[] = [];
    for (let minutes = 0; minutes < MINUTES_PER_DAY; minutes += TIME_SLOT_MINUTES) {
        const label = toTimeLabel(minutes);

        if (!restrict || !isSlotBefore(day, label, earliest)) {
            slots.push(label);
        }
    }

    if (keepValue && !slots.includes(keepValue) && !isSlotBefore(day, keepValue, earliest)) {
        return [keepValue, ...slots].sort((left, right) => left.localeCompare(right));
    }

    return slots;
};

type TimeSlotSelectProps = {
    id: string;
    day: Date | undefined;
    earliest: Date;
    value: string;
    onSelect: (time: string) => void;
};

/**
 * Offers the times still available on `day`. Only the listed times can be chosen, so a disallowed
 * time is not reachable rather than merely rejected after the fact. Disabled until a day is picked.
 */
const TimeSlotSelect = ({ id, day, earliest, value, onSelect }: TimeSlotSelectProps) => {
    const slots = timeSlotsFor(day, earliest, value);
    // The id stays the canonical 24-hour slot; only the label is 12-hour.
    const options = slots.map(slot => ({ id: slot, label: toTwelveHourLabel(slot) }));
    const isSelectable = day !== undefined && options.length > 0;

    return (
        <Select
            id={id}
            placeholder='--:-- --'
            options={options}
            value={slots.includes(value) ? [value] : []}
            onChange={option => {
                if (option) {
                    onSelect(option.id);
                }
            }}
            disabled={!isSelectable}
            useFilter
        />
    );
};

const DateTimeRangeField = ({ id, label, placeholder, value, onChange, notBefore }: DateTimeRangeFieldProps) => {
    const now = new Date();

    // The window may open no earlier than now, and no earlier than the caller's floor when it sets
    // one. Their later bound governs both the calendar and the time lists.
    const notBeforeDate = notBefore === undefined ? undefined : toDate(notBefore);
    const earliest = notBeforeDate !== undefined && notBeforeDate.getTime() > now.getTime() ? notBeforeDate : now;
    const earliestDay = startOfDay(earliest);

    const emitRange = (range: DateRange | undefined) =>
        onChange({ from: toIso(range?.from), to: toIso(closeOpenEndedDay(range?.to)) });

    const applyAllowedTime = (date: Date | undefined, time: string): Date | undefined => {
        const next = applyTime(date, time);

        return next !== undefined && next.getTime() < earliest.getTime() ? date : next;
    };

    // The picker resyncs its own draft whenever this prop changes identity, so a fresh object on every
    // render would wipe times the user has picked but not yet applied - the eligible-vehicles request
    // settling is enough to trigger it. Kept stable while the stored instants are unchanged.
    const range = useMemo(() => toRange(value), [value.from, value.to]);

    const hasUnusableEndpoint = isEndpointUnusable(value.from, earliest) || isEndpointUnusable(value.to, earliest);
    const errorMessage =
        notBefore === undefined ? 'Choose a date and time in the future.' : 'Choose a date and time after the pickup.';

    return (
        <DayPicker
            id={id}
            mode='range'
            placeholder={placeholder}
            inputProps={{ 'aria-label': label }}
            locale={DATE_LOCALE}
            dateFormat={DATE_TIME_FORMAT}
            value={range}
            onChange={emitRange}
            selectionBehavior='deferred'
            closeOnSelect={false}
            closeOnOutsideClick={false}
            isClearable
            // Days before the floor cannot be picked; the floor's own day stays open for a later time.
            disabledDays={{ before: earliestDay }}
            errorMessage={hasUnusableEndpoint ? errorMessage : undefined}
            rightSidebarClassName='padding-15'
            rightSidebar={({ draftValue, setDraftValue }) => {
                const startValue = toTimeValue(draftValue?.from);
                const endValue = toTimeValue(draftValue?.to);

                return (
                    <div className='display-flex flex-column gap-15 width-150'>
                        <div>
                            <FormLabel htmlFor={`${id}-start-time`}>Start time</FormLabel>
                            <TimeSlotSelect
                                id={`${id}-start-time`}
                                day={draftValue?.from}
                                earliest={earliest}
                                value={startValue}
                                onSelect={time =>
                                    setDraftValue({
                                        from: applyAllowedTime(draftValue?.from, time),
                                        to: draftValue?.to,
                                    })
                                }
                            />
                        </div>
                        <div>
                            <FormLabel htmlFor={`${id}-end-time`}>End time</FormLabel>
                            <TimeSlotSelect
                                id={`${id}-end-time`}
                                day={draftValue?.to}
                                earliest={earliest}
                                value={endValue}
                                onSelect={time =>
                                    setDraftValue({
                                        from: draftValue?.from,
                                        to: applyAllowedTime(draftValue?.to, time),
                                    })
                                }
                            />
                        </div>
                    </div>
                );
            }}
            footer={({ close, apply }) => (
                <ButtonToolbar className='justify-content-end'>
                    <Button bsStyle={Button.MUTED} onClick={close}>
                        Cancel
                    </Button>
                    <Button bsStyle={Button.PRIMARY} onClick={apply}>
                        Apply
                    </Button>
                </ButtonToolbar>
            )}
        />
    );
};

export default DateTimeRangeField;
